import { useCallback, useRef, useState } from 'react';
import { useLocale } from '../i18n/LocaleContext';

/** In-app alerts/confirms — never Telegram native popups. */
export function useGameAlert() {
  const { t } = useLocale();
  const [alert, setAlert] = useState(null);
  const resolveRef = useRef(null);

  const closeAlert = useCallback(() => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setAlert(null);
    if (r) r(false);
  }, []);

  const confirmAlert = useCallback(() => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setAlert(null);
    if (r) r(true);
  }, []);

  const showInfo = useCallback((opts) => {
    setAlert({
      icon: opts.icon || 'bulb.png',
      title: opts.title || '',
      body: opts.body || '',
      confirmLabel: opts.confirmLabel,
    });
  }, []);

  const showConfirm = useCallback(
    (opts) =>
      new Promise((resolve) => {
        if (resolveRef.current) {
          try {
            resolveRef.current(false);
          } catch (_) {
            /* ignore */
          }
        }
        resolveRef.current = resolve;
        setAlert({
          icon: opts.icon || 'bulb.png',
          title: opts.title || '',
          body: opts.body || '',
          confirmLabel: opts.confirmLabel || t('gotIt'),
          cancelLabel: opts.cancelLabel || t('close'),
          destructive: Boolean(opts.destructive),
          mode: 'confirm',
        });
      }),
    [t]
  );

  const showError = useCallback(
    (err, fallbackTitle) => {
      const data = err?.data || {};
      const code = data.code || '';
      const currency =
        data.currency ||
        (code.includes('amber') ? 'amber' : code.includes('coins') ? 'coins' : null);
      const isFunds = Boolean(currency) || code.startsWith('insufficient_');
      setAlert({
        icon: isFunds ? 'cross.png' : 'bulb.png',
        title: isFunds
          ? currency === 'amber'
            ? t('notEnoughAmber')
            : currency === 'coins'
              ? t('notEnoughCoins')
              : t('notEnoughTitle')
          : fallbackTitle || t('bootError'),
        body: isFunds ? '' : err?.message || String(err),
        have: data.have,
        need: data.need,
        currency: currency || undefined,
      });
      try {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      } catch (_) {
        /* ignore */
      }
    },
    [t]
  );

  return { alert, closeAlert, confirmAlert, showError, showInfo, showConfirm, setAlert };
}
